import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import DualLineCanvas from "@/components/DualLineCanvas";
import CountUp from "@/components/CountUp";
import ArtworkCard from "@/components/ArtworkCard";
import SEOHead from "@/components/SEOHead";
import { TID } from "@/constants/testIds";
import { getArtworks, getPress } from "@/lib/api";

const CREDIBILITY = [
  "Good Morning America",
  "Ripley's Believe It or Not!",
  "Henry Ford Health",
  "The Detroit News",
  "Art21",
  "ABC News",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [press, setPress] = useState([]);

  useEffect(() => {
    getArtworks({ featured: true }).then(setFeatured).catch(() => {});
    getPress(true).then((rows) => setPress(rows.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <>
      <SEOHead
        title="Colin Darke | The Ambidextrous Artist"
        description="Colin Darke is a Detroit-based American artist making the process the piece — often with both hands at once. Four decades of practice, featured by Good Morning America and Ripley's Believe It or Not!"
        path="/"
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <DualLineCanvas />
        <div className="mx-auto max-w-[1400px] w-full px-4 sm:px-8 lg:px-16 pb-16 lg:pb-24 relative z-10">
          <p className="catalog-label mb-6" data-testid="hero-eyebrow">
            Colin Darke · Detroit, Michigan · Over four decades
          </p>
          <h1
            data-testid={TID.heroTagline}
            className="display-serif text-[13vw] sm:text-[9vw] lg:text-[7.2vw] max-w-[16ch]"
          >
            Making the work
            <br />
            <span className="text-[hsl(var(--vermillion))] italic">
              in front of you.
            </span>
            <br />
            For four decades.
          </h1>
          <div className="mt-8 max-w-xl">
            <p className="text-lg text-[hsl(var(--ink-muted))] leading-relaxed">
              Colin Darke has spent more than forty years challenging himself
              to make the process the piece. What most artists finish behind
              a studio door, Colin finishes in front of you — often with
              both hands moving at once.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/techniques"
                data-testid={TID.heroWatchBtn}
                className="inline-flex items-center gap-3 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors"
              >
                <Play size={14} /> Watch him work
              </Link>
              <Link
                to="/commission"
                data-testid={TID.heroCommissionBtn}
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-[hsl(var(--ink))] px-6 py-3 hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors"
              >
                Commission a portrait <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY MARQUEE */}
      <section
        className="border-y border-[hsl(var(--line))] bg-[hsl(var(--bg-secondary))] overflow-hidden py-6"
        data-testid={TID.credibilityStrip}
      >
        <div className="flex items-center gap-4 px-4 sm:px-8 lg:px-16">
          <span className="catalog-label whitespace-nowrap">As featured in</span>
          <div className="marquee-track">
            {[...CREDIBILITY, ...CREDIBILITY].map((name, i) => (
              <div
                key={i}
                className="flex items-center gap-10 pr-10 whitespace-nowrap"
              >
                <span className="font-serif text-xl sm:text-2xl text-[hsl(var(--ink))]/80">
                  {name}
                </span>
                <span className="w-2 h-2 rotate-45 bg-[hsl(var(--vermillion))]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE WOW MOMENT */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="catalog-label mb-4">The performance</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
              The making of the piece
              <br />
              <span className="italic text-[hsl(var(--vermillion))]">
                is the piece.
              </span>
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed">
              Sometimes Colin's hands mirror one another to build a single
              image simultaneously. Sometimes they work independently,
              drawing two entirely different subjects at once. In both
              cases, the process happens in front of the viewer — no
              mirrors, no post-production, no duplicated video.
            </p>
            <Link
              to="/techniques"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest ink-underline pb-1"
            >
              See the four techniques <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <figure className="relative" data-testid={TID.wowMirroredVideo}>
              <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "9/16" }}>
                <iframe
                  src="https://www.youtube.com/embed/7WKmAlL12Jg"
                  title="Colin Darke on Good Morning America — ambidextrous artist"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <figcaption className="mt-3 catalog-label">
                Good Morning America · ABC News · 2021
              </figcaption>
            </figure>
            <figure className="relative md:mt-16" data-testid={TID.wowIndependentVideo}>
              <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "9/16" }}>
                <iframe
                  src="https://www.youtube.com/embed/ZOuhCDn9HWI"
                  title="Colin Darke — ambidextrous drawing (YouTube Short)"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <figcaption className="mt-3 catalog-label">
                Independent drawing · viral short
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="bg-[hsl(var(--bg-vault))] text-[hsl(var(--bg))] py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label text-[hsl(var(--bg))]/60 mb-6">Impact</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-16 max-w-3xl leading-[1.05]">
            An audience the traditional gallery system could not have built.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <Stat testId={TID.statHundredsMillions} label="Views across platforms" big="Hundreds of Millions" />
            <Stat
              testId={TID.statPerVideo}
              label="Peak per video"
              big={<><CountUp end={10} />M+</>}
            />
            <Stat
              testId={TID.statTiktok}
              label="Peak TikTok audience"
              big={<><CountUp end={700} />K</>}
            />
            <Stat
              testId={TID.statHands}
              label="Hands working independently"
              big={<><CountUp end={2} />
                <span className="text-[hsl(var(--vermillion))]"> / 1</span>
              </>}
              sub="artist"
            />
          </div>
          <p className="mt-16 catalog-label text-[hsl(var(--bg))]/50 max-w-xl">
            Aggregate view figures sourced to published coverage; individual
            peak metrics reflect reported periods.
          </p>
        </div>
      </section>

      {/* FEATURED ARTWORK */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <p className="catalog-label mb-3">Featured artwork</p>
              <h2 className="font-serif text-4xl sm:text-5xl">Selected works</h2>
            </div>
            <Link
              to="/artwork"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest ink-underline pb-1"
            >
              View the full archive <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((a, i) => (
              <ArtworkCard key={a.id} artwork={a} priority={i < 2 ? "high" : "medium"} />
            ))}
          </div>
        </div>
      </section>

      {/* DETROIT NEWS PULL QUOTE */}
      <section className="py-24 sm:py-32 bg-[hsl(var(--bg-secondary))] relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <blockquote className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.1] max-w-4xl">
            <span className="text-[hsl(var(--vermillion))]">"</span>
            Bold, colorful style reminiscent of the German Expressionists.
            <span className="text-[hsl(var(--vermillion))]">"</span>
          </blockquote>
          <p className="catalog-label mt-6">— The Detroit News, on Colin Darke</p>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="w-full bg-[hsl(var(--bg-secondary))] flex items-center justify-center overflow-hidden" style={{ aspectRatio: "4/5" }}>
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_ambidextrous-art/artifacts/a2vm0n7d_Colin%20and%20his%20sister%20Kelly%20%28also%20an%20artist%29.jpg"
                alt="Colin Darke with his sister and fellow artist Kelly Darke at a gallery exhibition."
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="catalog-label mb-4">About the artist</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
              The two-handed technique is a process.
              <span className="italic text-[hsl(var(--vermillion))]">
                {" "}It isn't the point.
              </span>
            </h2>
            <p className="mt-6 text-lg text-[hsl(var(--ink-muted))] leading-relaxed">
              Long before millions of people watched his hands move
              simultaneously across a page, Colin was drawing, painting,
              studying art, and participating in Detroit's art community. His
              work moves between drawing, painting, portraiture, abstraction,
              and experimentation.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest ink-underline pb-1"
            >
              Read the full biography <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SELECTED PRESS */}
      <section className="py-24 sm:py-32 bg-[hsl(var(--bg-secondary))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="catalog-label mb-3">Selected press</p>
              <h2 className="font-serif text-4xl sm:text-5xl">
                Where his work has been seen
              </h2>
            </div>
            <Link
              to="/press"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest ink-underline pb-1"
            >
              All coverage <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {press.map((p) => (
              <a
                key={p.id}
                href={p.source_url || "#"}
                target={p.source_url ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group flex gap-5 bg-[hsl(var(--surface))] border border-[hsl(var(--line))] p-5 hover:border-[hsl(var(--ink))] transition-colors"
              >
                {p.thumbnail && (
                  <img
                    src={p.thumbnail}
                    alt={p.headline}
                    className="w-24 h-24 object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <p className="catalog-label">
                    {p.publication} · {p.type}
                  </p>
                  <h3 className="font-serif text-xl leading-tight mt-1 line-clamp-2">
                    {p.headline}
                  </h3>
                  <p className="text-sm text-[hsl(var(--ink-muted))] mt-2 line-clamp-2">
                    {p.description}
                  </p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="ml-auto text-[hsl(var(--ink-muted))] group-hover:text-[hsl(var(--vermillion))] transition-colors flex-shrink-0"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 text-center">
          <p className="catalog-label mb-6">Philosophy</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            We are all
            <br />
            <span className="italic text-[hsl(var(--vermillion))]">artists.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-[hsl(var(--ink-muted))] leading-relaxed">
            Creativity isn't reserved for a small group of people who have been
            designated "artists." Make things regularly. Experiment with
            mediums. Allow yourself to make without excessive self-editing.
          </p>
        </div>
      </section>

      {/* COMMISSION CTA */}
      <section className="relative py-24 sm:py-32 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="catalog-label text-[hsl(var(--bg))]/60 mb-4">
              Commission
            </p>
            <h2 className="font-serif text-4xl sm:text-6xl leading-[1.05]">
              A portrait, drawn in front of you.
              <br />
              A performance you get to keep.
            </h2>
            <p className="mt-5 catalog-label text-[hsl(var(--bg))]/70">
              $5,000 flat · any commission under 5 ft · +$2,000 for the ambidextrous creation video
            </p>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[hsl(var(--bg))]/80 mb-6">
              Colin takes a limited number of commissioned portraits each
              year — single subject, a couple rendered one per hand, or a
              family study made in a single two-handed session.
            </p>
            <Link
              to="/commission"
              className="inline-flex items-center gap-3 bg-[hsl(var(--vermillion))] text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--bg))] hover:text-[hsl(var(--ink))] transition-colors"
              data-testid="cta-commission"
            >
              Start a commission <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ testId, label, big, sub }) {
  return (
    <div data-testid={testId}>
      <p className="catalog-label text-[hsl(var(--bg))]/50 mb-3">{label}</p>
      <p className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-none">
        {big}
      </p>
      {sub && (
        <p className="catalog-label text-[hsl(var(--bg))]/50 mt-3">{sub}</p>
      )}
    </div>
  );
}
